import { Card, CardContent } from '@/common/components/Card.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import ApplicantDetails from './AplicantDetails.tsx'
import StudentDetails from './StudentDetails.tsx'
import HouseholdDetails from './HouseHoldDetails.tsx'
import Chat from '@/pages/tasks/NextUpQueue/Chat.tsx'
import { useEffect, useRef, useState } from 'react'
import { useGetNextTask } from '@/pages/tasks/hooks/useGetAdminInfo.ts'
import { useQuery } from '@tanstack/react-query'
import { getFamilyApplication } from '@/queries/family_applications/requests.ts'
import ApplicationDecisionPopup from '../../applicants/applicant_details/ApplicationDecisionPopup/ApplicationDecisionPopup'
import { FamilyApplicationDetails } from '@/queries/family_applications/requests.ts'
import { PulsingEllipsesLoader } from '@/common/components/PulsingEllipsesLoader.tsx'
import { useUnassignTask } from '@/pages/tasks/hooks/useGetUnassingnTask.ts'
import LoadingCard from '@/common/components/LoadingCard.tsx'
// import AppAlertComponent from '@/common/components/AppAlertComponent.tsx'
import { useAppAlert } from '../../../../layouts/AppAlertProvider'
import { useAssignTask } from '../hooks/useAssignTask'
import { useNavigate } from 'react-router-dom'
import { AppRoutePaths } from '@/routing/route_paths'
import { useDispatch, useSelector } from 'react-redux'
import { setTask, setActiveTask, setErrorTasK } from '@/storage/tasks/slice.ts'
import { RootState } from '@/storage/store.ts'
import { useEscalateTask } from '@/pages/tasks/hooks/useEscalateTask.ts'
import { toast } from '@/shad/ui/toast/use-toast.ts'
import { AxiosError } from 'axios'
import { LogUserActivity } from '@/apis/users/requests.ts'
import { LOG_TYPES } from '@/common/constants.ts'

const NextUpQueue = () => {
  const auth = useSelector((state: RootState) => state.auth)
  const { selectedProgramYear } = useSelector(
    (state: RootState) => state.programs
  )
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false)

  const TEN_MINUTES = 1000 * 60 * 10
  const ONE_MINUTE = 1000 * 60

  const { fireAlert } = useAppAlert()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { task, activeTask } = useSelector((state: RootState) => state?.tasks)
  const { roles } = useSelector((state: RootState) => state?.auth)

  const taskRef = useRef(task)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isMounted = useRef(true)

  const TASK_ERROR = 'There are no more pending tasks assigned to your role.'

  const {
    mutate: getNextTask,
    data: nextTaskData,
    isSuccess: isSuccessGetTask,
    isError: isErrorGetTask,
    error: errorNextTask,
  } = useGetNextTask()

  const { mutate: unassignTask } = useUnassignTask()
  const { mutate: assignTask } = useAssignTask()
  const { mutate: scalateTask, isSuccess: isSuccessScalateTask } =
    useEscalateTask()

  const errorMessage =
    isErrorGetTask &&
    (errorNextTask as AxiosError<{ error: { message: string } }>)?.response
      ?.data?.error?.message

  const {
    data: familyApplicationData,
    isFetching,
    refetch,
  } = useQuery<FamilyApplicationDetails>({
    queryKey: [
      'familyApplication',
      task?.scholarship_application_id,
      task?.program_id,
    ],
    queryFn: () =>
      getFamilyApplication(
        task?.scholarship_application_id
          ? String(task.scholarship_application_id)
          : undefined,
        task?.program_id
      ),
    enabled: !!(task?.scholarship_application_id && task?.program_id),
    refetchOnMount: true,
  })

  let show_scalate_task = true
  if (auth.roles.includes('admin')) {
    show_scalate_task = false
  }

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Viewed the Next-UP Queue',
      })
      hasLoggedPageView.current = true
    }
  }, [])

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      unassignTask()
    }
  }, [])

  useEffect(() => {
    taskRef.current = task
  }, [task])

  useEffect(() => {
    if (isSuccessScalateTask) {
      toast({
        title: 'Task Escalated',
        description:
          'The task has been successfully escalated for further attention.',
      })
      getNextTask({
        program_id: selectedProgramYear?.id ?? 0,
        validate_assigned_task: 0,
      })
    }
  }, [isSuccessScalateTask])

  useEffect(() => {
    if (isSuccessGetTask) {
      startContinueWithTaskTimer()

      dispatch(setTask(nextTaskData))
      dispatch(setErrorTasK(false))
      refetch()
    }

    if (isErrorGetTask) {
      fireAlert({
        variant: 'warning',
        confirmBtnText: 'OK',
        denyBtn: false,
        message: `${errorMessage}`,
      }).then(async ({ confirmed }) => {
        if (confirmed && errorMessage === TASK_ERROR) {
          dispatch(setTask(nextTaskData))
          dispatch(setErrorTasK(true))
        }
      })
    }
  }, [isSuccessGetTask, isErrorGetTask, dispatch, nextTaskData])

  useEffect(() => {
    getNextTask({
      program_id: selectedProgramYear?.id ?? 0,
      validate_assigned_task: 0,
    })
  }, [selectedProgramYear?.id, getNextTask])

  const handleChatClick = (): void => {
    setIsChatOpen((prevIsChatOpen: boolean) => !prevIsChatOpen)
  }

  const handleButtonClick = () => {
    getNextTask({
      program_id: selectedProgramYear?.id ?? 0,
      validate_assigned_task: 1,
    })
    dispatch(setActiveTask(true))
  }

  const handleScalateTask = () => {
    scalateTask(taskRef?.current?.id ?? 0)
  }

  const startContinueWithTaskTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)

    intervalRef.current = setInterval(() => {
      if (isMounted.current) {
        handleInactivity()
        clearInterval(intervalRef.current as NodeJS.Timeout)
      }
    }, TEN_MINUTES)
  }

  const handleInactivity = () => {
    fireAlert({
      variant: 'warning',
      confirmBtnText: 'Continue',
      denyBtnText: 'No',
      denyBtn: true,
      message: 'Do you want to continue with this task?',
    }).then(async ({ confirmed }) => {
      if (!isMounted.current) return
      if (confirmed) {
        startContinueWithTaskTimer()
        assignTask(taskRef?.current?.id ?? 0)
      } else {
        handleRefreshNextTask()
      }
    })

    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
      if (isMounted.current) {
        handleRefreshNextTask()
      }
    }, ONE_MINUTE)
  }

  const handleRefreshNextTask = (): void => {
    unassignTask(undefined, {
      onSuccess: () => {
        fireAlert({
          variant: 'warning',
          confirmBtnText: 'Refresh',
          message:
            'You have been unassigned this task. Please click Refresh to get the next task.',
        }).then(async ({ confirmed }) => {
          if (confirmed) {
            getNextTask({
              program_id: selectedProgramYear?.id ?? 0,
              validate_assigned_task: 0,
            })
            startContinueWithTaskTimer()
          } else {
            navigate(AppRoutePaths.tasks.index)
          }
        })
      },
      onError: () => {},
    })
  }

  const handleUnassingTask = () => {
    try {
      unassignTask(undefined, {
        onSuccess: () => {
          if (roles[0] === 'customer_support') {
            toast({
              title: 'Unassigned Task',
              description:
                'click “Get Next Task” to retrieve the next task in the queue.',
            })
            dispatch(setActiveTask(false))
            return
          }
          navigate(AppRoutePaths.tasks.index)
        },
        onError: () => {},
      })
    } catch (error) {
      console.error(error)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function getFlagById(flags: Array<any>, id: number) {
    if (!flags || !Array.isArray(flags)) {
      return null
    }
    return flags.find((flag) => flag.id === id) || null
  }

  const specificFlag = getFlagById(
    familyApplicationData?.flags ?? [],
    nextTaskData?.flag?.id
  )

  if (!isSuccessGetTask && !isErrorGetTask)
    return <LoadingCard title="Loading Task" />

  return (
    <>
      {isErrorGetTask && errorMessage === TASK_ERROR ? (
        <div className="h-[70vh] flex items-center justify-center">
          <p>{errorMessage}</p>
        </div>
      ) : (
        <>
          <Card className="shadow-md mb-6">
            <CardContent className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Task</h2>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  onClick={() => handleUnassingTask()}
                  className="h-9 w-32 disabled:hover:bg-primary disabled:opacity-70"
                  disabled={isFetching}
                >
                  {isFetching ? (
                    <PulsingEllipsesLoader size="md" color="white" />
                  ) : (
                    'Unassign task'
                  )}
                </Button>
                {show_scalate_task && (
                  <Button
                    variant="outline"
                    className="h-9 w-32 disabled:hover:bg-primary disabled:opacity-70"
                    onClick={() => handleScalateTask()}
                  >
                    Escalate Task
                  </Button>
                )}
                <Button
                  onClick={() => handleButtonClick()}
                  className="h-9 w-32 disabled:hover:bg-primary disabled:opacity-70"
                  disabled={isFetching}
                >
                  {isFetching ? (
                    <PulsingEllipsesLoader size="md" color="white" />
                  ) : (
                    ' Get Next Task'
                  )}
                </Button>
              </div>
            </CardContent>
            <hr className="mt-2 border-gray-200" />
          </Card>

          {activeTask && (
            <div className="space-y-6">
              {specificFlag && (
                <Card className="shadow-md">
                  <CardContent className="space-y-6">
                    <div
                      className="flex justify-between gap-4"
                      key={specificFlag.id}
                    >
                      <div className=" w-[70%]">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {specificFlag.flag_name}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          {specificFlag.flag_description}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="primary"
                          size="sm"
                          className="py-1 px-3"
                          onClick={handleChatClick}
                        >
                          Chat
                        </Button>
                        <ApplicationDecisionPopup
                          flag={specificFlag}
                          application={specificFlag}
                          refetch={refetch}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
                <section
                  id="applicant_details"
                  className="w-full lg:col-span-1"
                >
                  <ApplicantDetails
                    selectedStudent={familyApplicationData?.students}
                    application={familyApplicationData}
                  />
                </section>

                <section id="students_details" className="w-full lg:col-span-1">
                  <StudentDetails
                    selectedStudent={familyApplicationData?.students}
                    application={familyApplicationData}
                  />
                </section>

                <section id="house_details" className="lg:col-span-2 w-full">
                  <HouseholdDetails application={familyApplicationData} />
                </section>
              </div>
            </div>
          )}
        </>
      )}
      {isChatOpen && <Chat handleChatClick={handleChatClick} />}
    </>
  )
}

export default NextUpQueue
